// Screen — Accounts registry (Wave 9.6).
//
// The cross-tenant pipeline-accounts table over GET /admin/accounts with the
// 9.6 server-side filters (client / agency / status / linked request / exact-
// or-prefix advertiser-id search). Row operations:
//
//   - Change status — the A7 dialog (POST /admin/accounts/{id}/status). The
//     enum (active|whitelisted|paused|banned) and the reason-required rule for
//     paused/banned are enforced client-side for fast feedback AND server-side
//     (handler-enforced, D2).
//   - Whitelist toggle — POST /admin/accounts/{id}/whitelist. This flips ops
//     pipeline metadata ONLY; it is NEVER the proxy allowlist (backend D12).
//
// Add account mirrors the TelegramChatBinding validate-gates-save pattern:
// Save is physically locked until the CURRENT account_id value passed the
// dry-run BC validation (POST /admin/accounts/validate) — any edit re-locks.
// The backend re-validates on the actual POST anyway (defense in depth) and
// its 422 refusal is rendered exactly like a validate failure.
//
// SECURITY: everything here is ops metadata about the operator's own pipeline;
// no master token, no proxy-allowlist writes, sanitized errors only.

import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { CheckIcon } from "lucide-react";
import { toast } from "sonner";

import {
  ACCOUNT_STATUSES,
  addToAllowlist,
  asBCRefusal,
  createAccount,
  isUnboundClient,
  listAccounts,
  setAccountStatus,
  setAccountWhitelist,
  statusNeedsReason,
  validateAccount,
  type AccountRow,
  type BCValidation,
} from "@/api/accounts";
import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  Modal,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AccountDrawer } from "./AccountDrawer";
import { NativeSelect, Pager } from "./controls";

const PAGE_SIZE = 50;

interface ClientRef {
  id: string;
  name: string;
}
interface AgencyRef {
  id: string;
  name: string;
}

function statusTone(status: string): Tone {
  switch (status) {
    case "active":
      return "credit";
    case "whitelisted":
      return "brand";
    case "paused":
      return "warn";
    case "banned":
      return "danger";
    default:
      return "na";
  }
}

// The BC-validation outcome panel — shared by the dry-run validate and the
// server's 422 enforcement refusal (rendered identically).
export function BCValidationPanel({ result }: { result: BCValidation }) {
  if (result.ok) {
    return (
      <div className="rounded-md bg-nx-credit-soft px-3 py-2 text-xs text-nx-credit">
        <p className="flex items-start gap-1.5 font-medium">
          <CheckIcon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Linked to Nexova BC — balance {result.balance} {result.currency} (
            {result.balance_kind})
            {result.tiktok_status
              ? `, TikTok status ${result.tiktok_status}`
              : ""}
          </span>
        </p>
        {result.stale && (
          <p className="mt-1">
            Note: the BC cache is stale — this balance may be out of date.
          </p>
        )}
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger"
    >
      <p className="font-medium">
        {result.reason === "not_in_bc"
          ? "Not linked to the Nexova BC (not_in_bc)."
          : "Linked, but the balance is not visible (balance_not_visible)."}
        {result.tiktok_status ? ` TikTok status: ${result.tiktok_status}.` : ""}
      </p>
      <p className="mt-1">
        {result.hint ??
          "Share the account to the Nexova BC first, then re-validate"}
      </p>
    </div>
  );
}

export function AccountsPage() {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [clients, setClients] = useState<ClientRef[]>([]);
  const [agencies, setAgencies] = useState<AgencyRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Distinct from `busy` (the Ops-WL toggle) so the two per-row actions don't
  // disable each other.
  const [allowBusy, setAllowBusy] = useState<string | null>(null);

  // Filters — every change re-queries the backend (server-side filtering) and
  // resets the pager to the first page.
  const [clientF, setClientF] = useState("");
  const [agencyF, setAgencyF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [statusFor, setStatusFor] = useState<AccountRow | null>(null);
  // The HUB detail drawer — a clicked row opens here.
  const [drawerRow, setDrawerRow] = useState<AccountRow | null>(null);

  // Swap an updated row into the list AND the open drawer so both stay in sync
  // after any mutation (status, allowlist, provisioning, attribution, unlink).
  function applyRowUpdate(updated: AccountRow) {
    setAccounts((prev) =>
      prev.map((a) => (a.id === updated.id ? updated : a)),
    );
    setDrawerRow((prev) =>
      prev && prev.id === updated.id ? updated : prev,
    );
  }

  function clientName(id: string): string {
    if (!id) return "—";
    return clients.find((c) => c.id === id)?.name ?? id;
  }
  function agencyName(id: string): string {
    if (!id) return "—";
    return agencies.find((a) => a.id === id)?.name ?? id;
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAccounts({
        client_id: clientF,
        agency_id: agencyF,
        status: statusF,
        q: q.trim(),
        limit: PAGE_SIZE,
        offset,
      });
      setAccounts(res.accounts ?? []);
    } catch (err) {
      setError(errMessage(err));
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [clientF, agencyF, statusF, q, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Name lookups are tolerant — a failure must not blank the registry.
  useEffect(() => {
    void Promise.allSettled([
      api.get<{ clients: ClientRef[] }>("/admin/clients"),
      api.get<{ agencies: AgencyRef[] }>("/admin/agencies"),
    ]).then(([c, a]) => {
      if (c.status === "fulfilled") setClients(c.value.clients ?? []);
      if (a.status === "fulfilled") setAgencies(a.value.agencies ?? []);
    });
  }, []);

  async function toggleWhitelist(row: AccountRow) {
    setBusy(row.id);
    try {
      const res = await setAccountWhitelist(row.id, !row.whitelist);
      applyRowUpdate({ ...row, whitelist: res.whitelist });
      toast.success(
        res.whitelist ? "Marked provisioned" : "Provisioning cleared",
      );
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  // Explicit, audited one-click merge into the owning client's PROXY allowlist.
  // 409 unbound_client means the row is not attributed to a client yet.
  async function addRowToAllowlist(row: AccountRow) {
    setAllowBusy(row.id);
    try {
      const res = await addToAllowlist(row.id);
      applyRowUpdate({ ...row, in_client_allowlist: res.in_client_allowlist });
      toast.success("Added to the client's proxy allowlist");
    } catch (err) {
      if (isUnboundClient(err)) {
        toast.error("Attribute this account to a client first.");
      } else {
        toast.error(errMessage(err));
      }
    } finally {
      setAllowBusy(null);
    }
  }

  const columns: Column<AccountRow>[] = [
    {
      key: "account",
      header: "Ads account #",
      cell: (r) =>
        r.account_id ? (
          <span className="font-mono text-xs text-nx-text tabular-nums">
            {r.account_id}
          </span>
        ) : (
          <span className="text-xs text-nx-text-3">— pending</span>
        ),
    },
    {
      key: "client",
      header: "Client",
      cell: (r) => (
        <span className="text-nx-text-2">{clientName(r.client_id)}</span>
      ),
    },
    {
      key: "agency",
      header: "Agency",
      cell: (r) => (
        <span className="text-nx-text-2">{agencyName(r.agency_id)}</span>
      ),
    },
    {
      key: "geo",
      header: "Geo",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2">{r.geo || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => (
        <div className="grid gap-0.5">
          <ToneBadge
            tone={statusTone(r.status_account)}
            className="w-fit capitalize"
          >
            {r.status_account || "—"}
          </ToneBadge>
          {statusNeedsReason(r.status_account) && r.status_reason && (
            <span
              title={r.status_reason}
              className="max-w-44 truncate text-xs text-nx-text-3"
            >
              {r.status_reason}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "whitelist",
      // Provisioning-execution status (accounts.whitelist) — deliberately NOT
      // proxy access. The word "Allowlist" is reserved for the proxy gate (next
      // col); this flag only tracks whether ops has provisioned the account.
      header: (
        <span title="Provisioning-execution status (accounts.whitelist) — NOT proxy access">
          Provisioning
        </span>
      ),
      cell: (r) => (
        <div className="flex items-center gap-2">
          {r.whitelist ? (
            <ToneBadge tone="brand">Prov</ToneBadge>
          ) : (
            <span className="text-nx-text-3">—</span>
          )}
          <Button
            variant="ghost"
            size="xs"
            disabled={busy === r.id}
            aria-label={`Provisioning ${r.whitelist ? "off" : "on"} ${r.id}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleWhitelist(r);
            }}
          >
            {r.whitelist ? "Remove" : "Add"}
          </Button>
        </div>
      ),
    },
    {
      key: "allowlist",
      // The PROXY allowlist gate — whether the owning client can see & recharge
      // this account. Only meaningful for a delivered row with an owning client.
      header: (
        <span title="Proxy allowlist — whether the owning client can see & recharge this account">
          Allowlist
        </span>
      ),
      cell: (r) => {
        if (!r.client_id || !r.account_id) {
          return <span className="text-nx-text-3">—</span>;
        }
        if (r.in_client_allowlist) {
          return (
            <ToneBadge tone="credit" className="w-fit">
              In allowlist ✓
            </ToneBadge>
          );
        }
        return (
          <div className="grid max-w-56 gap-1">
            <span
              role="alert"
              className="text-xs font-medium text-nx-danger"
            >
              NOT in allowlist ⚠ — client can&apos;t see or recharge this account
            </span>
            <Button
              variant="outline"
              size="xs"
              className="w-fit"
              disabled={allowBusy === r.id}
              aria-label={`Add ${r.id} to allowlist`}
              onClick={(e) => {
                e.stopPropagation();
                addRowToAllowlist(r);
              }}
            >
              {allowBusy === r.id ? "Adding…" : "Add to allowlist"}
            </Button>
          </div>
        );
      },
    },
    {
      key: "shared",
      header: "Shared",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-3 tabular-nums">
          {r.shared_at || "—"}
        </span>
      ),
    },
    {
      key: "request",
      header: "Request",
      cell: (r) =>
        r.request_id ? (
          <Link
            to={`/account-requests?request=${r.request_id}`}
            title={r.request_id}
            className="font-mono text-xs text-nx-brand underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.request_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <Button
          variant="outline"
          size="xs"
          disabled={busy === r.id}
          onClick={(e) => {
            e.stopPropagation();
            setStatusFor(r);
          }}
        >
          Change status…
        </Button>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Accounts</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            The ad-account pipeline registry. Adding an account with a TikTok
            advertiser ID requires a passing Nexova-BC link validation.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>Add account</Button>
      </div>

      {/* Filter bar — every filter is applied SERVER-SIDE via query params. */}
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <NativeSelect
          aria-label="Filter by client"
          value={clientF}
          onChange={(e) => {
            setClientF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Filter by agency"
          value={agencyF}
          onChange={(e) => {
            setAgencyF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">All agencies</option>
          {agencies.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Filter by status"
          value={statusF}
          onChange={(e) => {
            setStatusF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">Any status</option>
          {ACCOUNT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </NativeSelect>
        <Input
          aria-label="Search account ID"
          placeholder="Search account #…"
          value={q}
          className="h-8 font-mono"
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
        />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load accounts</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={accounts}
            rowKey={(r) => r.id}
            onRowClick={(r) => setDrawerRow(r)}
            empty="No accounts match these filters."
          />
          <Pager
            offset={offset}
            pageSize={PAGE_SIZE}
            count={accounts.length}
            loading={loading}
            onOffset={setOffset}
          />
        </>
      )}

      <AccountDrawer
        row={drawerRow}
        agencies={agencies}
        clientName={clientName}
        agencyName={agencyName}
        onOpenChange={(o) => {
          if (!o) setDrawerRow(null);
        }}
        onRowUpdated={applyRowUpdate}
        onChangeStatus={(r) => setStatusFor(r)}
      />

      <StatusDialog
        account={statusFor}
        onOpenChange={(o) => {
          if (!o) setStatusFor(null);
        }}
        onChanged={(updated) => {
          applyRowUpdate(updated);
          setStatusFor(null);
        }}
      />

      <AddAccountModal
        open={addOpen}
        clients={clients}
        agencies={agencies}
        onOpenChange={setAddOpen}
        onCreated={async () => {
          setAddOpen(false);
          await load();
        }}
      />
    </AdminShell>
  );
}

// ---- Change-status dialog (A7) ------------------------------------------------

function StatusDialog({
  account,
  onOpenChange,
  onChanged,
}: {
  account: AccountRow | null;
  onOpenChange: (open: boolean) => void;
  onChanged: (updated: AccountRow) => void;
}) {
  const [status, setStatus] = useState<string>("active");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectId = useId();
  const reasonId = useId();

  // Re-seed when a new row opens.
  useEffect(() => {
    if (!account) return;
    setStatus(
      (ACCOUNT_STATUSES as readonly string[]).includes(account.status_account)
        ? account.status_account
        : "active",
    );
    setReason(account.status_reason ?? "");
    setError(null);
  }, [account]);

  const needsReason = statusNeedsReason(status);
  const reasonMissing = needsReason && reason.trim() === "";

  async function save() {
    if (!account || reasonMissing || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setAccountStatus(
        account.id,
        status,
        reason.trim(),
      );
      toast.success(`Account status set to ${status}`);
      onChanged(updated);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={account !== null}
      onOpenChange={onOpenChange}
      title="Change account status"
      description={
        account
          ? `${account.account_id || "advertiser-less row"} · currently ${account.status_account || "—"}`
          : undefined
      }
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={reasonMissing || saving} onClick={save}>
            {saving ? "Saving…" : "Save status"}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label
            htmlFor={selectId}
            className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
          >
            Status
          </label>
          <NativeSelect
            id={selectId}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {ACCOUNT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label
            htmlFor={reasonId}
            className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
          >
            Reason{needsReason ? " (required)" : ""}
          </label>
          <Input
            id={reasonId}
            value={reason}
            aria-invalid={reasonMissing}
            placeholder={
              needsReason ? "Why is this account paused/banned?" : "Optional"
            }
            onChange={(e) => setReason(e.target.value)}
          />
          {reasonMissing && (
            <p role="alert" className="mt-1 text-xs font-medium text-nx-danger">
              A reason is required for paused or banned.
            </p>
          )}
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs font-medium text-nx-danger"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---- Add-account modal (validate-gates-save) -----------------------------------

function AddAccountModal({
  open,
  clients,
  agencies,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  clients: ClientRef[];
  agencies: AgencyRef[];
  onOpenChange: (open: boolean) => void;
  onCreated: () => void | Promise<void>;
}) {
  const idAccount = useId();
  const idClient = useId();
  const idAgency = useId();
  const idGeo = useId();
  const idProduct = useId();

  const [accountId, setAccountId] = useState("");
  const [clientId, setClientId] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [geo, setGeo] = useState("");
  const [productUrl, setProductUrl] = useState("");

  // `validated` records the exact account_id a validation ran against — Save
  // enables only when it matches the current input AND passed (the
  // TelegramChatBinding pattern).
  const [validated, setValidated] = useState<{
    value: string;
    result: BCValidation;
  } | null>(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  const trimmed = accountId.trim();
  const canValidate = trimmed !== "" && !validating;
  const canSave =
    validated !== null &&
    validated.value === trimmed &&
    validated.result.ok &&
    !saving &&
    !validating;

  function reset() {
    setAccountId("");
    setClientId("");
    setAgencyId("");
    setGeo("");
    setProductUrl("");
    setValidated(null);
  }

  async function onValidate() {
    if (!canValidate) return;
    setValidating(true);
    try {
      const result = await validateAccount(trimmed);
      setValidated({ value: trimmed, result });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setValidating(false);
    }
  }

  async function onSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      await createAccount({
        account_id: trimmed,
        client_id: clientId,
        agency_id: agencyId,
        geo: geo.trim(),
        product_url: productUrl.trim(),
        platform: "tiktok",
        status_account: "active",
      });
      toast.success(`Account ${trimmed} added`);
      reset();
      await onCreated();
    } catch (err) {
      // The backend re-validated and refused (422) — render it exactly like a
      // validate failure. ok=false also re-locks Save.
      const refusal = asBCRefusal(err);
      if (refusal) {
        setValidated({ value: trimmed, result: refusal });
      } else {
        toast.error(errMessage(err));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title="Add account"
      description="Register a TikTok ad account in the pipeline. The advertiser ID must be linked to the Nexova BC — validate before saving."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={!canValidate}
            onClick={onValidate}
          >
            {validating ? "Validating…" : "Validate"}
          </Button>
          <Button disabled={!canSave} onClick={onSave}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label
            htmlFor={idAccount}
            className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
          >
            Ads account # (advertiser ID)
          </label>
          <Input
            id={idAccount}
            value={accountId}
            spellCheck={false}
            autoComplete="off"
            inputMode="numeric"
            placeholder="7000000000000"
            className="font-mono"
            onChange={(e) => {
              setAccountId(e.target.value);
              // Any edit invalidates the prior validation — Save re-locks
              // until the NEW value passes a validate.
              setValidated(null);
            }}
          />
        </div>

        {validated && validated.value === trimmed && (
          <BCValidationPanel result={validated.result} />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor={idClient}
              className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
            >
              Client
            </label>
            <NativeSelect
              id={idClient}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">No client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label
              htmlFor={idAgency}
              className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
            >
              Agency
            </label>
            <NativeSelect
              id={idAgency}
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
            >
              <option value="">No agency</option>
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label
              htmlFor={idGeo}
              className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
            >
              Geo
            </label>
            <Input
              id={idGeo}
              value={geo}
              placeholder="US"
              onChange={(e) => setGeo(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor={idProduct}
              className="mb-1 block text-xs font-medium tracking-wide text-nx-text-2 uppercase"
            >
              Product URL
            </label>
            <Input
              id={idProduct}
              value={productUrl}
              placeholder="https://…"
              onChange={(e) => setProductUrl(e.target.value)}
            />
          </div>
        </div>

        <p className="text-xs text-nx-text-3">
          Save unlocks only after the current advertiser ID passes the BC-link
          validation. The server re-validates on save (defense in depth).
        </p>
      </div>
    </Modal>
  );
}
